import React, { Component } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UsageKindChip, UsageStatusChip, UsageApiChip, UsageDurationBar, fmtTime, fmtDateTime, fmtMs, fmtInt } from './usage-widgets';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Uma VISITA à tela: cabeçalho com o resumo, expandir carrega os requests filhos (lazy).
// loadRows(screenVisitId) => Promise<rows>. onRowClick(row) abre o drawer.
// ---------------------------------------------------------------------------

export default class UsageVisitAccordion extends Component {
  constructor(props) {
    super(props);
    this.state = { expanded: false, loading: false, rows: null };
    this.toggle = this.toggle.bind(this);
  }

  toggle() {
    const next = !this.state.expanded;
    this.setState({ expanded: next });
    if (next && this.state.rows === null && this.props.loadRows) {
      this.setState({ loading: true });
      this.props.loadRows(this.props.visit.screenVisitId)
        .then((rows) => this.setState({ rows: rows || [], loading: false }))
        .catch(() => this.setState({ rows: [], loading: false }));
    }
  }

  render() {
    const { intl, visit, onRowClick, onUserClick } = this.props;
    const { expanded, loading, rows } = this.state;
    const children = (rows || []).filter((r) => r.actionKind !== 'screen');
    const maxMs = Math.max(1000, ...children.map((r) => Number(r.durationMs) || 0));
    const span = visit.lastAt ? Math.max(0, new Date(visit.lastAt) - new Date(visit.occurredAt)) : 0;

    return (
      <Accordion expanded={expanded} onChange={this.toggle} style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 8, boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10, width: '100%' }}>
            <Typography variant="body2" style={{ fontVariantNumeric: 'tabular-nums', color: '#666', minWidth: 150 }}>{fmtDateTime(visit.occurredAt)}</Typography>
            <UsageKindChip intl={intl} kind="screen" label={t(intl, 'usage.visit.screen', 'tela')} />
            <Tooltip title={visit.url || ''}><Typography variant="body2" style={{ fontWeight: 600 }}>{visit.screen}</Typography></Tooltip>
            {/* Nome do usuário só vem na listagem POR TELA; na linha do tempo o usuário é o filtro. */}
            {visit.userName || visit.userId ? (
              <Chip size="small" label={visit.userName || `#${visit.userId}`} style={{ background: '#009688', color: '#fff', cursor: onUserClick ? 'pointer' : 'default' }}
                onClick={onUserClick ? (e) => { e.stopPropagation(); onUserClick(visit); } : undefined} />
            ) : null}
            <Box flex={1} />
            <Chip size="small" variant="outlined" label={`${fmtInt(visit.requests)} ${t(intl, 'usage.visit.req', 'req')}`} />
            <Chip size="small" variant="outlined" label={`${fmtMs(span)} ${t(intl, 'usage.visit.onScreen', 'na tela')}`} />
            <Chip size="small" variant="outlined" label={`${fmtMs(visit.sumDurationMs)} ${t(intl, 'usage.visit.apiTime', 'de API')}`} />
            {visit.writes > 0 && <Chip size="small" label={`${visit.writes} ${t(intl, 'usage.visit.writes', 'escrita(s)')}`} style={{ background: '#8BC34A', color: '#fff' }} />}
            {visit.exports > 0 && <Chip size="small" label={`${visit.exports} ${t(intl, 'usage.visit.exports', 'exportação(ões)')}`} style={{ background: '#FF9800', color: '#fff' }} />}
            {visit.denials > 0 && <Chip size="small" label={`${visit.denials} ${t(intl, 'usage.visit.denials', 'recusa(s)')}`} style={{ background: '#F44336', color: '#fff' }} />}
            {visit.errors > 0 && <Chip size="small" label={`${visit.errors} ${t(intl, 'usage.visit.errors', 'erro(s)')}`} style={{ background: '#B71C1C', color: '#fff' }} />}
            <Typography variant="caption" style={{ color: '#999' }}>{visit.ip}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress size={22} color="secondary" /></Box>}
          {!loading && rows && children.length === 0 && <Typography variant="body2" style={{ color: '#999', padding: 12 }}>{t(intl, 'usage.visit.none', 'Nenhum request registrado nesta visita.')}</Typography>}
          {!loading && children.length > 0 && (
            <Box style={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t(intl, 'usage.col.time', 'Hora')}</TableCell><TableCell>{t(intl, 'usage.col.action', 'Ação')}</TableCell><TableCell>API</TableCell><TableCell>{t(intl, 'usage.col.route', 'Rota')}</TableCell>
                    <TableCell>{t(intl, 'usage.col.entity', 'Entidade')}</TableCell><TableCell>Status</TableCell><TableCell>{t(intl, 'usage.col.duration', 'Duração')}</TableCell><TableCell>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {children.map((r) => (
                    <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => onRowClick && onRowClick(r)}>
                      <TableCell style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtTime(r.occurredAt, true)}</TableCell>
                      <TableCell><UsageKindChip intl={intl} kind={r.actionKind} /></TableCell>
                      <TableCell><UsageApiChip api={r.api} /></TableCell>
                      <TableCell><Tooltip title={r.url || r.path || ''}><span>{r.route}</span></Tooltip></TableCell>
                      <TableCell>{r.entityType}{r.entityId ? <b> #{r.entityId}</b> : ''}</TableCell>
                      <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                      <TableCell><UsageDurationBar ms={r.durationMs} max={maxMs} /></TableCell>
                      <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}{r.details && r.details.forwarded ? <span style={{ color: '#c62828' }}> ⚠</span> : null}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
}
